import { useState, useMemo, useEffect, useRef, lazy, Suspense } from "react";
import DOMPurify from "dompurify";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Loader2, MessageSquare, Plus, Send, ArrowLeft, UserPlus, Zap, Search, Clock, Calendar, Mail, Settings, Link as LinkIcon, Inbox, BarChart3, UserCheck, Star, TrendingUp, ArrowDown, Filter, Copy, Check, ExternalLink, XCircle, CheckCircle2, Webhook, Key, ChevronDown, ChevronUp, FileCode, Eye, Trash2, X, Sparkles, Pencil, ListChecks, Download, PhoneOff } from "lucide-react";
import type { Lead, LeadMessage, Appointment, NurtureStep } from "@shared/schema";
import RogersVoice from "@/components/rogers-voice";
import EmailMarketing from "@/pages/email-marketing";
import { STATUS_STYLES, STATUS_OPTIONS, STATUS_COLORS, CADENCE_OPTIONS } from "./lead-desk/constants";
import NurtureTemplatePicker from "./lead-desk/NurtureTemplatePicker";
import SiteAgentTranscript from "./lead-desk/SiteAgentTranscript";
import RogersOpsPanel from "./lead-desk/RogersOpsPanel";

const FunnelTab = lazy(() => import("./lead-desk/FunnelTab"));
const AppointmentsTab = lazy(() => import("./lead-desk/AppointmentsTab"));
const AnalyticsTab = lazy(() => import("./lead-desk/AnalyticsTab"));
const EmailTemplateManager = lazy(() => import("./lead-desk/EmailTemplateManager"));
const SiteAgentConversations = lazy(() => import("./lead-desk/SiteAgentConversations"));

const SOURCE_LABELS: Record<string, string> = { manual: 'Manual', referral: 'Referral', linkedin: 'LinkedIn', 'cold-email': 'Cold-email', 'site-agent': 'Site Agent', funnel: 'Funnel' };

type LeadDeskTab = "leads" | "inbox" | "appointments" | "analytics" | "funnel" | "email" | "nurture" | "site-agent";

export default function LeadDesk() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const qc = useQueryClient();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<LeadDeskTab>("leads");
  const [showForm, setShowForm] = useState(false);
  const [showCaptureCard, setShowCaptureCard] = useState(false);
  const [captureApiKey, setCaptureApiKey] = useState<string | null>(null);
  const [captureKeyCopied, setCaptureKeyCopied] = useState(false);
  const [captureUrlCopied, setCaptureUrlCopied] = useState(false);
  const [testingLead, setTestingLead] = useState(false);
  const [showCaptureKey, setShowCaptureKey] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [source, setSource] = useState("manual");

  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [nurtureInput, setNurtureInput] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string | null>(null);

  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [apptTitle, setApptTitle] = useState("");
  const [apptStartAt, setApptStartAt] = useState("");
  const [apptEndAt, setApptEndAt] = useState("");
  const [apptMeetingLink, setApptMeetingLink] = useState("");

  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailPrompt, setEmailPrompt] = useState("");
  const [nurturePlanOpen, setNurturePlanOpen] = useState(true);
  const [memoryPanelOpen, setMemoryPanelOpen] = useState(false);
  const [copiedStepIdx, setCopiedStepIdx] = useState<number | null>(null);
  const [replyInput, setReplyInput] = useState("");
  const [replyMode, setReplyMode] = useState<"nurture" | "reply">("nurture");
  const [warRoomDismissedIds, setWarRoomDismissedIds] = useState<Set<number>>(new Set());
  const [lastReplyIntent, setLastReplyIntent] = useState<string | null>(null);
  const [lastBookingUrl, setLastBookingUrl] = useState<string | null>(null);
  const [expandedEmailIds, setExpandedEmailIds] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [editingStepIdx, setEditingStepIdx] = useState<number | null>(null);
  const [editingStepSubject, setEditingStepSubject] = useState("");
  const [editingStepBody, setEditingStepBody] = useState("");
  const [editingStepImageUrl, setEditingStepImageUrl] = useState("");

  // Inbox reply form state
  const [inboxReplyLeadId, setInboxReplyLeadId] = useState<number | null>(null);
  const [inboxReplyText, setInboxReplyText] = useState("");

  // Sparse lead enrichment form state
  const [showEnrichForm, setShowEnrichForm] = useState(false);
  const [enrichIndustry, setEnrichIndustry] = useState("");
  const [enrichCompany, setEnrichCompany] = useState("");
  const [enrichPainPoints, setEnrichPainPoints] = useState("");

  const chatEndRef = useRef<HTMLDivElement>(null);

  const { data: devModeResponse } = useQuery<{ devMode: boolean }>({
    queryKey: ["/api/system/dev-mode"],
    queryFn: async () => {
      const res = await fetch("/api/system/dev-mode");
      if (!res.ok) return { devMode: false };
      return res.json();
    },
    staleTime: 60_000,
  });
  const isDevMode = devModeResponse?.devMode ?? import.meta.env.DEV;

  const { data: currentWorkspace } = useQuery<any>({
    queryKey: ["/api/workspaces", activeWorkspaceId],
    queryFn: async () => {
      if (!activeWorkspaceId) return null;
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    staleTime: 60_000,
  });

  const { data: leads, isLoading, isError: leadsError } = useQuery<Lead[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "leads"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/leads`, { credentials: "include" });
      if (res.status === 403) throw new Error("You don't have permission to view leads in this workspace.");
      if (!res.ok) throw new Error("Failed to fetch leads");
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });

  const { data: messagesData, isLoading: messagesLoading } = useQuery<LeadMessage[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "leads", selectedLead?.id, "messages"],
    queryFn: async () => {
      if (!selectedLead) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/leads/${selectedLead.id}/messages`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch messages");
      return res.json();
    },
    enabled: !!selectedLead,
    refetchInterval: selectedLead ? 3000 : false,
  });

  const { data: leadEmails = [] } = useQuery<any[]>({
    queryKey: ["/api/leads", selectedLead?.id, "emails"],
    queryFn: async () => {
      if (!selectedLead) return [];
      const res = await fetch(`/api/leads/${selectedLead.id}/emails`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!selectedLead,
    refetchInterval: selectedLead ? 5000 : false,
  });

  // T002: Lead memories query
  const { data: leadMemories = [] } = useQuery<any[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "leads", selectedLead?.id, "memories"],
    queryFn: async () => {
      if (!selectedLead) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/leads/${selectedLead.id}/memories`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!selectedLead && memoryPanelOpen,
  });

  const { data: pipelineData } = useQuery<{ total: number; byStage: Record<string, number>; overdueFollowUps: number; hottestLead: { id: number; name: string; score: number } | null; activeLeads: number; estimatedPipelineValue: number }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "rogers", "pipeline"],
    queryFn: async () => {
      if (!activeWorkspaceId) return null;
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/rogers/pipeline`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    refetchInterval: 30000,
  });

  const { data: unifiedMessages, isLoading: inboxLoading } = useQuery<any[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "messages", "unified"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/messages/unified`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch unified messages");
      return res.json();
    },
    enabled: !!activeWorkspaceId && activeTab === "inbox",
  });

  const { data: appointmentsData } = useQuery<Appointment[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "appointments"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/appointments`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch appointments");
      return res.json();
    },
    enabled: !!activeWorkspaceId && !!selectedLead,
  });

  const { data: rogersAgentQuestions = [] } = useQuery<any[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "agent-questions", "rogers"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/agent-questions`, { credentials: "include" });
      if (!res.ok) return [];
      const all = await res.json();
      return (all || []).filter((q: any) => q.agent === "Rogers" && q.status === "pending" && q.question?.includes("HOT LEAD ALERT"));
    },
    enabled: !!activeWorkspaceId,
    refetchInterval: 30000,
  });

  const leadAppointments = useMemo(() => {
    if (!appointmentsData || !selectedLead) return [];
    return appointmentsData.filter((a) => a.leadId === selectedLead.id);
  }, [appointmentsData, selectedLead]);

  useEffect(() => {
    if (activeWorkspaceId) {
      fetch(`/api/workspaces/${activeWorkspaceId}/api-key`, { credentials: "include" })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data?.apiKey) setCaptureApiKey(data.apiKey); })
        .catch(() => {});
    }
  }, [activeWorkspaceId]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messagesData]);

  const createLeadMutation = useMutation({
    mutationFn: async (data: { name: string; email: string; phone: string; source: string; workspaceId: number }) => {
      const res = await apiRequest("POST", `/api/workspaces/${data.workspaceId}/leads`, data);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads"] });
      setName("");
      setEmail("");
      setPhone("");
      setSource("manual");
      setShowForm(false);
    },
    onError: (err: any) => {
      toast({ title: "Failed to Add Lead", description: err.message || "Could not create lead.", variant: "destructive" });
    },
  });

  const updateLeadMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: number; status?: string; nurtureCadenceDays?: number; nextNurtureAt?: string | null; sequenceType?: string; preferredChannel?: string; templateId?: number | null }) => {
      const res = await apiRequest("PUT", `/api/leads/${id}`, data);
      return res.json();
    },
    onSuccess: (updatedLead: Lead) => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads"] });
      if (selectedLead && updatedLead.id === selectedLead.id) {
        setSelectedLead(updatedLead);
      }
    },
    onError: (err: any) => {
      toast({ title: "Update Failed", description: err.message || "Could not update lead.", variant: "destructive" });
    },
  });

  const deleteLeadMutation = useMutation({
    mutationFn: async (leadId: number) => {
      const res = await apiRequest("DELETE", `/api/leads/${leadId}`);
      return res.json();
    },
    onSuccess: () => {
      setSelectedLead(null);
      setShowDeleteConfirm(false);
      qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads"] });
      toast({ title: "Lead Deleted", description: "Lead has been permanently removed." });
    },
    onError: (err: any) => {
      toast({ title: "Delete Failed", description: err.message || "Could not delete lead.", variant: "destructive" });
    },
  });

  const blockLeadMutation = useMutation({
    mutationFn: async (leadId: number) => {
      const res = await apiRequest("PUT", `/api/leads/${leadId}`, { status: "OPTED_OUT" });
      return res.json();
    },
    onSuccess: (updatedLead: Lead) => {
      setShowBlockConfirm(false);
      qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads"] });
      if (selectedLead && updatedLead.id === selectedLead.id) setSelectedLead(updatedLead);
      toast({ title: "Lead Blocked", description: "Lead opted out — they will no longer receive nurture emails." });
    },
    onError: (err: any) => {
      toast({ title: "Block Failed", description: err.message || "Could not block lead.", variant: "destructive" });
    },
  });

  const nurtureMutation = useMutation({
    mutationFn: async (data: { leadId: number; message: string }) => {
      const res = await apiRequest("POST", `/api/leads/${data.leadId}/nurture`, { message: data.message });
      return res.json();
    },
    onSuccess: () => {
      if (selectedLead) {
        qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads", selectedLead.id, "messages"] });
        qc.invalidateQueries({ queryKey: ["/api/leads", selectedLead.id, "emails"] });
        qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads"] });
      }
    },
    onError: (err: any) => {
      toast({ title: "Nurture Failed", description: err.message || "Could not send nurture message.", variant: "destructive" });
    },
  });

  const replyMutation = useMutation({
    mutationFn: async (data: { leadId: number; content: string; channel: string }) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/leads/${data.leadId}/reply`, { content: data.content, channel: data.channel });
      return res.json();
    },
    onSuccess: (result) => {
      setReplyInput("");
      if (result.detectedIntent) setLastReplyIntent(result.detectedIntent);
      if (result.bookingUrl) setLastBookingUrl(result.bookingUrl);
      if (selectedLead) {
        qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads", selectedLead.id, "messages"] });
        qc.invalidateQueries({ queryKey: ["/api/leads", selectedLead.id, "emails"] });
        qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads"] });
        qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "rogers", "pipeline"] });
      }
      if (result.aiSkipped) {
        toast({
          title: "Reply logged",
          description: result.detectedIntent && result.detectedIntent !== "NEUTRAL"
            ? `Intent detected: ${result.detectedIntent.replace(/_/g, " ")}. Rogers will respond when AI is enabled.`
            : "Message saved. Rogers will respond when AI is enabled.",
        });
      } else {
        toast({
          title: "Rogers responded",
          description: result.detectedIntent && result.detectedIntent !== "NEUTRAL"
            ? `Intent detected: ${result.detectedIntent.replace(/_/g, " ")}${result.bookingUrl ? " — booking link created!" : ""}`
            : "Reply processed and email queued.",
        });
      }
    },
    onError: (err: any) => {
      toast({ title: "Reply Failed", description: err.message || "Could not process reply.", variant: "destructive" });
    },
  });

  const inboxReplyMutation = useMutation({
    mutationFn: async (data: { leadId: number; content: string }) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/leads/${data.leadId}/reply`, { content: data.content, channel: "email" });
      return res.json();
    },
    onSuccess: (result) => {
      setInboxReplyText("");
      qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "messages", "unified"] });
      qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads"] });
      toast({
        title: result.aiSkipped ? "Reply logged" : "Rogers responded",
        description: result.detectedIntent && result.detectedIntent !== "NEUTRAL"
          ? `Intent: ${result.detectedIntent.replace(/_/g, " ")}${result.bookingUrl ? " — booking link created!" : ""}`
          : "Reply processed. Rogers will send an AI response.",
      });
    },
    onError: (err: any) => {
      toast({ title: "Reply Failed", description: err.message || "Could not process reply.", variant: "destructive" });
    },
  });

  const enrichLeadMutation = useMutation({
    mutationFn: async (data: { leadId: number; industry: string; company: string; painPoints: string }) => {
      const existingCf = ((selectedLead as any)?.customFields as Record<string, string>) || {};
      const customFields: Record<string, string> = { ...existingCf };
      if (data.industry) customFields._prospect_industry = data.industry;
      if (data.company) customFields._prospect_company = data.company;
      if (data.painPoints) customFields._prospect_pain_points = data.painPoints;
      const res = await apiRequest("PUT", `/api/leads/${data.leadId}`, { customFields });
      return res.json();
    },
    onSuccess: (updatedLead: Lead) => {
      setShowEnrichForm(false);
      setEnrichIndustry("");
      setEnrichCompany("");
      setEnrichPainPoints("");
      if (selectedLead && updatedLead.id === selectedLead.id) setSelectedLead(updatedLead);
      qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads"] });
      toast({ title: "Lead Updated", description: "Rogers will use this context to personalize future messages." });
    },
    onError: (err: any) => {
      toast({ title: "Update Failed", description: err.message || "Could not update lead.", variant: "destructive" });
    },
  });

  const createAppointmentMutation = useMutation({
    mutationFn: async (data: {
      workspaceId: number;
      leadId: number;
      title: string;
      startAt: string;
      endAt: string;
      meetingLink?: string;
      attendeeName: string;
      attendeeEmail: string;
    }) => {
      const res = await apiRequest("POST", `/api/workspaces/${data.workspaceId}/appointments`, data);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "appointments"] });
      setShowAppointmentForm(false);
      setApptTitle("");
      setApptStartAt("");
      setApptEndAt("");
      setApptMeetingLink("");
    },
    onError: (err: any) => {
      toast({ title: "Booking Failed", description: err.message || "Could not create appointment.", variant: "destructive" });
    },
  });

  const sendEmailMutation = useMutation({
    mutationFn: async (data: { leadId: number; prompt?: string }) => {
      const res = await apiRequest("POST", `/api/leads/${data.leadId}/send-nurture-email`, { prompt: data.prompt || "" });
      return res.json();
    },
    onSuccess: () => {
      setShowEmailForm(false);
      setEmailPrompt("");
      toast({ title: "Draft Queued", description: "Email draft saved to your outbox." });
      if (selectedLead) {
        qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads", selectedLead.id, "messages"] });
        qc.invalidateQueries({ queryKey: ["/api/leads", selectedLead.id, "emails"] });
      }
    },
    onError: (err: any) => {
      toast({ title: "Draft Failed", description: err.message || "Could not queue email draft.", variant: "destructive" });
    },
  });

  const sendPrewrittenEmailMutation = useMutation({
    mutationFn: async (data: { leadId: number; subject?: string; body: string }) => {
      const res = await apiRequest("POST", `/api/leads/${data.leadId}/send-prewritten-email`, { subject: data.subject, body: data.body });
      return res.json();
    },
    onSuccess: (_data, variables) => {
      toast({ title: "Email Queued", description: "Pre-written email sent to outbox — no further action needed." });
      qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads", variables.leadId, "messages"] });
    },
    onError: (err: any) => {
      toast({ title: "Send Failed", description: err.message || "Could not queue email.", variant: "destructive" });
    },
  });

  const generateSequenceMutation = useMutation({
    mutationFn: async (leadId: number) => {
      const res = await apiRequest("POST", `/api/leads/${leadId}/generate-sequence`, {});
      return res.json();
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads"] });
      if (selectedLead && data.lead) {
        setSelectedLead(data.lead);
      }
      toast({ title: "Nurture Plan Generated", description: "5-step sequence created by Rogers." });
    },
    onError: (err: any) => {
      toast({ title: "Generation Failed", description: err.message || "Could not generate nurture plan.", variant: "destructive" });
    },
  });

  const updateStepStatusMutation = useMutation({
    mutationFn: async ({ leadId, stepIndex, status }: { leadId: number; stepIndex: number; status: string }) => {
      const res = await apiRequest("PATCH", `/api/leads/${leadId}/nurture-step/${stepIndex}`, { status });
      return res.json();
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads"] });
      if (selectedLead && data.lead) {
        setSelectedLead(data.lead);
      }
    },
    onError: (err: any) => {
      toast({ title: "Update Failed", description: err.message || "Could not update step status.", variant: "destructive" });
    },
  });

  const updateStepContentMutation = useMutation({
    mutationFn: async ({ leadId, stepIndex, subject, body, imageUrl }: { leadId: number; stepIndex: number; subject: string; body: string; imageUrl?: string }) => {
      const res = await apiRequest("PATCH", `/api/leads/${leadId}/nurture-step/${stepIndex}/content`, { subject, body, imageUrl });
      return res.json();
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads"] });
      if (selectedLead && data.lead) {
        setSelectedLead(data.lead);
      }
      setEditingStepIdx(null);
      toast({ title: "Step saved" });
    },
    onError: (err: any) => {
      toast({ title: "Save Failed", description: err.message || "Could not save step.", variant: "destructive" });
    },
  });

  const pauseNurtureMutation = useMutation({
    mutationFn: async ({ leadId, paused }: { leadId: number; paused: boolean }) => {
      const res = await apiRequest("PATCH", `/api/leads/${leadId}/nurture-pause`, { paused });
      return res.json();
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads"] });
      if (selectedLead && data.lead) {
        setSelectedLead(data.lead);
      }
      toast({ title: data.lead?.nurturePaused ? "Nurture paused" : "Nurture resumed" });
    },
    onError: (err: any) => {
      toast({ title: "Failed", description: err.message, variant: "destructive" });
    },
  });

  const approveEmailMutation = useMutation({
    mutationFn: async ({ wsId, emailId }: { wsId: number; emailId: number }) => {
      const res = await apiRequest("POST", `/api/workspaces/${wsId}/email-outbox/${emailId}/approve`, {});
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/leads", selectedLead?.id, "emails"] });
      toast({ title: "Email approved & sent" });
    },
    onError: (err: any) => {
      toast({ title: "Approve failed", description: err.message, variant: "destructive" });
    },
  });

  const discardEmailMutation = useMutation({
    mutationFn: async ({ wsId, emailId }: { wsId: number; emailId: number }) => {
      const res = await apiRequest("POST", `/api/workspaces/${wsId}/email-outbox/${emailId}/discard`, {});
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/leads", selectedLead?.id, "emails"] });
      toast({ title: "Email discarded" });
    },
    onError: (err: any) => {
      toast({ title: "Discard failed", description: err.message, variant: "destructive" });
    },
  });

  const { data: nurtureSettings } = useQuery<{ defaultCadenceDays: number; emailTonePreference: "professional" | "friendly" | "urgent"; requiresApproval: boolean }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "nurture-settings"],
    queryFn: async () => {
      if (!activeWorkspaceId) return { defaultCadenceDays: 3, emailTonePreference: "professional", requiresApproval: false };
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/nurture-settings`, { credentials: "include" });
      if (!res.ok) return { defaultCadenceDays: 3, emailTonePreference: "professional", requiresApproval: false };
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });

  const updateNurtureSettingsMutation = useMutation({
    mutationFn: async (data: { defaultCadenceDays?: number; emailTonePreference?: "professional" | "friendly" | "urgent"; requiresApproval?: boolean }) => {
      const res = await apiRequest("PATCH", `/api/workspaces/${activeWorkspaceId}/nurture-settings`, data);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "nurture-settings"] });
      toast({ title: "Nurture settings updated" });
    },
    onError: (err: any) => {
      toast({ title: "Update Failed", description: err.message, variant: "destructive" });
    },
  });

  const [sourceFilter, setSourceFilter] = useState<string | null>(null);

  const filteredLeads = useMemo(() => {
    if (!leads) return [];
    return leads.filter((lead) => {
      const matchesSearch =
        !searchQuery ||
        lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = !selectedStatusFilter || lead.status === selectedStatusFilter;
      const matchesSource = !sourceFilter || (lead.source || "") === sourceFilter;
      return matchesSearch && matchesStatus && matchesSource;
    });
  }, [leads, searchQuery, selectedStatusFilter, sourceFilter]);

  const statusCounts = useMemo(() => {
    if (!leads) return { NEW: 0, CONTACTED: 0, QUALIFIED: 0, CONVERTED: 0 };
    return {
      NEW: leads.filter((l) => l.status === "NEW").length,
      CONTACTED: leads.filter((l) => l.status === "CONTACTED").length,
      QUALIFIED: leads.filter((l) => l.status === "QUALIFIED").length,
      CONVERTED: leads.filter((l) => l.status === "CONVERTED").length,
    };
  }, [leads]);

  const getSuggestedStarters = (status: string): string[] => {
    const starters: Record<string, string[]> = {
      NEW: [
        "What's your biggest challenge right now?",
        "How did you hear about us?",
        "Can I send you more information?",
      ],
      CONTACTED: [
        "Did you have a chance to review what I sent?",
        "What questions do you have?",
        "Can we schedule a quick call?",
      ],
      QUALIFIED: [
        "When would be a good time to discuss next steps?",
        "What's your timeline for implementation?",
        "Let me prepare a custom proposal for you",
      ],
      CONVERTED: [
        "How is everything going so far?",
        "Do you need any additional support?",
        "Any feedback on your experience?",
      ],
    };
    return starters[status] || starters.NEW;
  };

  function handleCreateLead() {
    if (!name.trim() || !email.trim() || !activeWorkspaceId) return;
    createLeadMutation.mutate({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      source,
      workspaceId: activeWorkspaceId,
    });
  }

  function handleNurtureSend() {
    if (!nurtureInput.trim() || !selectedLead || !activeWorkspaceId) return;
    const msg = nurtureInput.trim();
    setNurtureInput("");
    nurtureMutation.mutate({
      leadId: selectedLead.id,
      message: msg,
    });
  }

  function handleReplySend() {
    if (!replyInput.trim() || !selectedLead) return;
    const msg = replyInput.trim();
    setReplyInput("");
    setLastReplyIntent(null);
    setLastBookingUrl(null);
    replyMutation.mutate({
      leadId: selectedLead.id,
      content: msg,
      channel: "email",
    });
  }

  function openLeadDetail(lead: Lead) {
    setSelectedLead(lead);
    setNurtureInput("");
    setShowAppointmentForm(false);
    setShowEmailForm(false);
  }

  function handleCadenceChange(days: number) {
    if (!selectedLead) return;
    const nextNurture = new Date();
    nextNurture.setDate(nextNurture.getDate() + days);
    updateLeadMutation.mutate({
      id: selectedLead.id,
      nurtureCadenceDays: days,
      nextNurtureAt: nextNurture.toISOString(),
    });
  }

  function handleBookAppointment() {
    if (!apptTitle.trim() || !apptStartAt || !apptEndAt || !selectedLead || !activeWorkspaceId) return;
    createAppointmentMutation.mutate({
      workspaceId: activeWorkspaceId,
      leadId: selectedLead.id,
      title: apptTitle.trim(),
      startAt: new Date(apptStartAt).toISOString(),
      endAt: new Date(apptEndAt).toISOString(),
      meetingLink: apptMeetingLink.trim() || undefined,
      attendeeName: selectedLead.name,
      attendeeEmail: selectedLead.email,
    });
  }

  if (!activeWorkspaceId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="no-workspace-state">
        <MessageSquare size={48} className="text-muted-foreground/50 mb-4" />
        <h2 className="text-xl font-display font-bold text-muted-foreground">No Workspace Selected</h2>
        <p className="text-muted-foreground/70 mt-2 text-sm">Select a workspace to manage leads.</p>
      </div>
    );
  }

  if (selectedLead) {
    const suggestedStarters = getSuggestedStarters(selectedLead.status);
    const messages = messagesData || [];
    return (
      <div className="space-y-6" data-testid="lead-detail-view">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            data-testid="button-back-to-leads"
            onClick={() => setSelectedLead(null)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm font-medium transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Leads
          </button>
          <div className="flex items-center gap-2">
            {selectedLead.status !== "OPTED_OUT" && (
              <button
                data-testid="button-block-lead"
                onClick={() => setShowBlockConfirm(true)}
                className="flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
              >
                <PhoneOff size={12} />
                Block / Opt Out
              </button>
            )}
            <button
              data-testid="button-delete-lead"
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-lg border border-border bg-muted/50 text-muted-foreground hover:text-red-400 hover:border-red-500/30 transition-colors"
            >
              <Trash2 size={12} />
              Delete
            </button>
          </div>
        </div>

        {/* Block confirmation dialog */}
        {showBlockConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="block-confirm-dialog">
            <div className="bg-card border border-border rounded-2xl p-6 max-w-sm w-full mx-4 space-y-4">
              <h3 className="text-base font-bold text-foreground">Block Lead?</h3>
              <p className="text-sm text-muted-foreground">
                This will set <strong>{selectedLead.name}</strong> to OPTED_OUT status. They will be permanently excluded from all Rogers nurture emails.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  data-testid="button-block-cancel"
                  onClick={() => setShowBlockConfirm(false)}
                  className="px-4 py-2 text-sm rounded-xl border border-border text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="button-block-confirm"
                  onClick={() => blockLeadMutation.mutate(selectedLead.id)}
                  disabled={blockLeadMutation.isPending}
                  className="px-4 py-2 text-sm rounded-xl bg-red-600/20 border border-red-500/30 text-red-400 hover:bg-red-600/30 transition-colors font-bold disabled:opacity-50"
                >
                  {blockLeadMutation.isPending ? "Blocking..." : "Block Lead"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirmation dialog */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="delete-confirm-dialog">
            <div className="bg-card border border-border rounded-2xl p-6 max-w-sm w-full mx-4 space-y-4">
              <h3 className="text-base font-bold text-foreground">Delete Lead?</h3>
              <p className="text-sm text-muted-foreground">
                This will permanently delete <strong>{selectedLead.name}</strong> and all associated messages. Previously sent emails remain in your outbox history. This cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  data-testid="button-delete-cancel"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm rounded-xl border border-border text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="button-delete-confirm"
                  onClick={() => deleteLeadMutation.mutate(selectedLead.id)}
                  disabled={deleteLeadMutation.isPending}
                  className="px-4 py-2 text-sm rounded-xl bg-red-600/20 border border-red-500/30 text-red-400 hover:bg-red-600/30 transition-colors font-bold disabled:opacity-50"
                >
                  {deleteLeadMutation.isPending ? "Deleting..." : "Delete Permanently"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="space-y-4">
            <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-4" data-testid="lead-info">
              <h2 className="text-lg font-display font-bold text-foreground">{selectedLead.name}</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span className="text-foreground/80" data-testid="lead-email">{selectedLead.email}</span>
                </div>
                {selectedLead.phone && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Phone</span>
                    <span className="text-foreground/80" data-testid="lead-phone">{selectedLead.phone}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Source</span>
                  <span className="text-foreground/80 flex items-center gap-1.5" data-testid="lead-source">
                    {selectedLead.source === "site-agent" && (
                      <span className="text-[9px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-1.5 py-0.5 rounded-full font-bold uppercase">Widget</span>
                    )}
                    {selectedLead.source}
                  </span>
                </div>
                {(selectedLead as any).utmCampaign && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ad Campaign</span>
                    <span className="text-foreground/80 text-xs" data-testid="lead-utm-campaign">{(selectedLead as any).utmCampaign}</span>
                  </div>
                )}
                {(selectedLead as any).sourcePageSlug && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Landing Page</span>
                    <span className="text-foreground/80 text-xs" data-testid="lead-page-slug">/p/{(selectedLead as any).sourcePageSlug}</span>
                  </div>
                )}
                {(selectedLead as any).utmSource && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Traffic Source</span>
                    <span className="text-foreground/80 text-xs" data-testid="lead-utm-source">{(selectedLead as any).utmSource}{(selectedLead as any).utmMedium ? ` / ${(selectedLead as any).utmMedium}` : ""}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Status</span>
                  {selectedLead.status === "hot" ? (
                    <span className="text-[10px] font-bold px-2 py-1 rounded-md border bg-red-500/20 text-red-400 border-red-500/40 animate-pulse uppercase" data-testid="badge-hot-detail">
                      HIGH PRIORITY
                    </span>
                  ) : (
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-md uppercase ${STATUS_STYLES[selectedLead.status] || "bg-muted/50 text-muted-foreground/70"}`}>
                      {selectedLead.status}
                    </span>
                  )}
                </div>
              </div>
              <div className="pt-3 border-t border-border space-y-2">
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Update Status</label>
                <div className="flex flex-wrap gap-1.5">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid={`button-lead-status-${s.toLowerCase()}`}
                      onClick={() => updateLeadMutation.mutate({ id: selectedLead.id, status: s })}
                      disabled={selectedLead.status === s || updateLeadMutation.isPending}
                      className={`text-[10px] font-bold px-2.5 py-1.5 rounded-lg transition-colors ${
                        selectedLead.status === s
                          ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                          : "bg-muted/50 text-muted-foreground/70 border border-border hover:text-foreground hover:border-border"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Site Agent Conversation Transcript */}
            {selectedLead.source === "site-agent" && <SiteAgentTranscript wsId={activeWorkspaceId} leadId={selectedLead.id} />}

            {/* Activity Summary */}
            {(() => {
              const sentEmails = leadEmails.filter((e: any) => e.status === "SENT" || e.status === "QUEUED").length;
              const lastEmailDate = leadEmails.length > 0
                ? leadEmails.reduce((latest: any, e: any) => {
                    const d = new Date(e.sentAt || e.createdAt || 0);
                    return d > new Date(latest.sentAt || latest.createdAt || 0) ? e : latest;
                  }, leadEmails[0])
                : null;
              const daysSinceContact = lastEmailDate
                ? Math.floor((Date.now() - new Date(lastEmailDate.sentAt || lastEmailDate.createdAt).getTime()) / (1000 * 60 * 60 * 24))
                : null;
              return (
                <div className="bg-card/50 border border-border rounded-2xl p-4 space-y-2.5" data-testid="activity-summary">
                  <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                    <TrendingUp size={12} className="text-blue-400" />
                    Activity
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center">
                      <p className="text-lg font-bold text-foreground" data-testid="text-emails-sent-count">{sentEmails}</p>
                      <p className="text-[10px] text-muted-foreground/70">emails sent</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-foreground" data-testid="text-days-since-contact">
                        {daysSinceContact !== null ? daysSinceContact : "—"}
                      </p>
                      <p className="text-[10px] text-muted-foreground/70">days ago</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[11px] font-bold text-foreground" data-testid="text-next-scheduled">
                        {selectedLead.nextNurtureAt ? new Date(selectedLead.nextNurtureAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
                      </p>
                      <p className="text-[10px] text-muted-foreground/70">next nurture</p>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-4" data-testid="nurture-cadence">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Settings size={14} className="text-muted-foreground" />
                  <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Nurture Cadence</h3>
                </div>
                <button
                  data-testid="button-toggle-nurture-pause"
                  onClick={() => pauseNurtureMutation.mutate({ leadId: selectedLead.id, paused: !selectedLead.nurturePaused })}
                  disabled={pauseNurtureMutation.isPending}
                  className={`flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1.5 rounded-lg transition-colors border ${
                    selectedLead.nurturePaused
                      ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                      : "bg-muted/50 text-muted-foreground/70 border-border hover:text-foreground"
                  }`}
                  title={selectedLead.nurturePaused ? "Resume Rogers for this lead" : "Pause Rogers for this lead"}
                >
                  {pauseNurtureMutation.isPending ? <Loader2 size={10} className="animate-spin" /> : null}
                  {selectedLead.nurturePaused ? "▶ Resume" : "⏸ Pause Outreach"}
                </button>
              </div>
              {selectedLead.nurturePaused && (
                <div className="text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2.5 py-1.5" data-testid="nurture-paused-notice">
                  Rogers outreach is paused for this lead.
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {CADENCE_OPTIONS.map((days) => (
                  <button
                    key={days}
                    data-testid={`button-cadence-${days}`}
                    onClick={() => handleCadenceChange(days)}
                    disabled={(updateLeadMutation.isPending || selectedLead.nurturePaused) ?? undefined}
                    className={`text-[10px] font-bold px-2.5 py-1.5 rounded-lg transition-colors ${
                      selectedLead.nurtureCadenceDays === days
                        ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                        : "bg-muted/50 text-muted-foreground/70 border border-border hover:text-foreground hover:border-border"
                    }`}
                  >
                    {days}d
                  </button>
                ))}
              </div>
              {selectedLead.nextNurtureAt && !selectedLead.nurturePaused && (
                <p className="text-[10px] text-muted-foreground/70">
                  Next nurture: {new Date(selectedLead.nextNurtureAt).toLocaleDateString()}
                </p>
              )}
            </div>

            <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-4" data-testid="nurture-settings">
              <div className="flex items-center gap-2">
                <Zap size={14} className="text-amber-400" />
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Nurture Settings</h3>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] text-muted-foreground/70 uppercase tracking-wider font-bold">Sequence Type</label>
                <div className="flex flex-wrap gap-1.5">
                  {([
                    { value: "appointment", label: "Appointment" },
                    { value: "awareness", label: "Awareness" },
                    { value: "coupon", label: "Coupon" },
                    { value: "email_list_growth", label: "Email List" },
                  ] as const).map(({ value: seq, label }) => (
                    <button
                      key={seq}
                      data-testid={`button-sequence-${seq}`}
                      onClick={() => updateLeadMutation.mutate({ id: selectedLead.id, sequenceType: seq })}
                      disabled={updateLeadMutation.isPending}
                      className={`text-[10px] font-bold px-2.5 py-1.5 rounded-lg transition-colors capitalize ${
                        (selectedLead as any).sequenceType === seq
                          ? "bg-amber-600/20 text-amber-400 border border-amber-500/30"
                          : "bg-muted/50 text-muted-foreground/70 border border-border hover:text-foreground hover:border-border"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] text-muted-foreground/70 uppercase tracking-wider font-bold">Channel</label>
                <div className="flex flex-wrap gap-1.5">
                  {(["email", "sms", "both"] as const).map((ch) => (
                    <button
                      key={ch}
                      data-testid={`button-channel-${ch}`}
                      onClick={() => updateLeadMutation.mutate({ id: selectedLead.id, preferredChannel: ch })}
                      disabled={updateLeadMutation.isPending}
                      className={`text-[10px] font-bold px-2.5 py-1.5 rounded-lg transition-colors uppercase ${
                        (selectedLead as any).preferredChannel === ch
                          ? "bg-cyan-600/20 text-cyan-400 border border-cyan-500/30"
                          : "bg-muted/50 text-muted-foreground/70 border border-border hover:text-foreground hover:border-border"
                      }`}
                    >
                      {ch}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] text-muted-foreground/70 uppercase tracking-wider font-bold">Interests / Services</label>
                <div className="flex flex-wrap gap-1">
                  {((selectedLead as any).interests || []).map((interest: string, idx: number) => (
                    <span key={idx} className="text-[10px] bg-muted/50 text-muted-foreground border border-border px-2 py-1 rounded-lg">
                      {interest}
                    </span>
                  ))}
                  {(!(selectedLead as any).interests || (selectedLead as any).interests?.length === 0) && (
                    <span className="text-[10px] text-muted-foreground/50">No interests tracked</span>
                  )}
                </div>
              </div>

              {(selectedLead as any).engagementScore !== undefined && (
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground/70 uppercase tracking-wider font-bold">Engagement</span>
                  <span className={`text-xs font-bold ${(selectedLead as any).engagementScore > 0 ? "text-emerald-400" : (selectedLead as any).engagementScore < 0 ? "text-red-400" : "text-muted-foreground"}`} data-testid="text-engagement-score">
                    {(selectedLead as any).engagementScore > 0 ? "+" : ""}{(selectedLead as any).engagementScore}
                  </span>
                </div>
              )}

              <NurtureTemplatePicker wsId={activeWorkspaceId} lead={selectedLead} onUpdate={(templateId) => updateLeadMutation.mutate({ id: selectedLead.id, templateId })} />
            </div>

            <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-3" data-testid="nurture-plan">
              <button
                onClick={() => setNurturePlanOpen(!nurturePlanOpen)}
                className="flex items-center justify-between w-full"
                data-testid="button-toggle-nurture-plan"
              >
                <div className="flex items-center gap-2">
                  <ListChecks size={14} className="text-emerald-400" />
                  <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Nurture Plan</h3>
                  {selectedLead.nurtureSequenceSource && (
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${selectedLead.nurtureSequenceSource === "auto" ? "bg-blue-500/10 text-blue-400 border-blue-500/20" : "bg-purple-500/10 text-purple-400 border-purple-500/20"}`} data-testid="badge-sequence-source">
                      {selectedLead.nurtureSequenceSource === "auto" ? "Auto" : "Manual"}
                    </span>
                  )}
                </div>
                {nurturePlanOpen ? <ChevronUp size={14} className="text-muted-foreground/50" /> : <ChevronDown size={14} className="text-muted-foreground/50" />}
              </button>
              {nurturePlanOpen && (
                <div className="space-y-3">
                  {selectedLead.nurtureSequence && Array.isArray(selectedLead.nurtureSequence) && selectedLead.nurtureSequence.length > 0 ? (
                    <div className="space-y-2" data-testid="nurture-steps">
                      {selectedLead.nurtureSequence.map((step, idx) => {
                        const channelIcon = step.channel === "sms" ? "SMS" : step.channel === "both" ? "Multi" : "Email";
                        const actionLabel = step.action.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());
                        return (
                          <div key={idx} className="bg-muted/30 border border-border rounded-xl px-3 py-2.5 space-y-1.5" data-testid={`nurture-step-${idx}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded">
                                  Day {step.day}
                                </span>
                                <span className="text-[10px] font-bold text-blue-400 bg-blue-500/10 border border-blue-500/20 px-1.5 py-0.5 rounded uppercase">
                                  {channelIcon}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  data-testid={`button-edit-step-${idx}`}
                                  onClick={() => {
                                    setEditingStepIdx(idx);
                                    setEditingStepSubject(step.subject || "");
                                    setEditingStepBody(step.body || "");
                                    setEditingStepImageUrl((step as any).imageUrl || "");
                                  }}
                                  className="text-muted-foreground/50 hover:text-blue-400 transition-colors"
                                  title="Edit step"
                                >
                                  <Pencil size={12} />
                                </button>
                                <button
                                  data-testid={`button-copy-step-${idx}`}
                                  onClick={() => {
                                    navigator.clipboard.writeText(`Subject: ${step.subject}\n\n${step.body}`);
                                    setCopiedStepIdx(idx);
                                    setTimeout(() => setCopiedStepIdx(null), 2000);
                                  }}
                                  className="text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                                >
                                  {copiedStepIdx === idx ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                                </button>
                              </div>
                            </div>
                            {editingStepIdx === idx ? (
                              <div className="space-y-2 pt-1" data-testid={`step-editor-${idx}`}>
                                <input
                                  data-testid={`input-step-subject-${idx}`}
                                  value={editingStepSubject}
                                  onChange={(e) => setEditingStepSubject(e.target.value)}
                                  className="w-full text-xs font-bold bg-muted/50 border border-blue-500/40 rounded-lg px-2 py-1.5 text-foreground focus:outline-none focus:border-blue-500"
                                  placeholder="Subject line"
                                />
                                <textarea
                                  data-testid={`input-step-body-${idx}`}
                                  value={editingStepBody}
                                  onChange={(e) => setEditingStepBody(e.target.value)}
                                  rows={4}
                                  className="w-full text-[11px] bg-muted/50 border border-blue-500/40 rounded-lg px-2 py-1.5 text-foreground leading-relaxed focus:outline-none focus:border-blue-500 resize-y"
                                  placeholder="Email body"
                                />
                                <div className="space-y-1">
                                  <label className="text-[9px] text-muted-foreground/60 uppercase tracking-wider font-bold">Image URL (optional)</label>
                                  <input
                                    data-testid={`input-step-image-${idx}`}
                                    value={editingStepImageUrl}
                                    onChange={(e) => setEditingStepImageUrl(e.target.value)}
                                    className="w-full text-[11px] bg-muted/50 border border-border rounded-lg px-2 py-1.5 text-foreground focus:outline-none focus:border-blue-500/40"
                                    placeholder="https://... (leave blank for text-only)"
                                  />
                                  {editingStepImageUrl && (
                                    <img src={editingStepImageUrl} alt="Preview" className="mt-1 max-h-20 rounded border border-border object-contain" data-testid={`img-step-preview-${idx}`} />
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 pt-0.5">
                                  <button
                                    data-testid={`button-save-step-${idx}`}
                                    onClick={() => selectedLead && updateStepContentMutation.mutate({ leadId: selectedLead.id, stepIndex: idx, subject: editingStepSubject, body: editingStepBody, imageUrl: editingStepImageUrl || undefined })}
                                    disabled={updateStepContentMutation.isPending}
                                    className="text-[9px] font-bold px-2 py-1 rounded bg-blue-500/15 text-blue-400 border border-blue-500/30 hover:bg-blue-500/25 transition-colors flex items-center gap-1"
                                  >
                                    {updateStepContentMutation.isPending ? <Loader2 size={9} className="animate-spin" /> : <Check size={9} />}
                                    Save
                                  </button>
                                  <button
                                    data-testid={`button-cancel-step-edit-${idx}`}
                                    onClick={() => setEditingStepIdx(null)}
                                    className="text-[9px] font-bold px-2 py-1 rounded bg-muted/50 text-muted-foreground border border-border hover:text-foreground transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="text-xs font-bold text-foreground/80">{step.subject}</div>
                                <div className="text-[11px] text-muted-foreground/70 leading-relaxed">{step.body}</div>
                                {(step as any).imageUrl && (
                                  <img src={(step as any).imageUrl} alt="Step image" className="max-h-16 rounded border border-border object-contain mt-1" data-testid={`img-step-${idx}`} />
                                )}
                              </>
                            )}
                        <div className="flex items-center justify-between">
                              <span className="text-[10px] text-amber-400/70 font-medium">{actionLabel}</span>
                              <div className="flex items-center gap-1.5">
                                {(!step.status || step.status === "upcoming") && (
                                  <>
                                    <button
                                      data-testid={`button-mark-sent-${idx}`}
                                      onClick={() => selectedLead && updateStepStatusMutation.mutate({ leadId: selectedLead.id, stepIndex: idx, status: "sent" })}
                                      disabled={updateStepStatusMutation.isPending}
                                      className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20 transition-colors"
                                    >
                                      Mark Sent
                                    </button>
                                    <button
                                      data-testid={`button-skip-step-${idx}`}
                                      onClick={() => selectedLead && updateStepStatusMutation.mutate({ leadId: selectedLead.id, stepIndex: idx, status: "skipped" })}
                                      disabled={updateStepStatusMutation.isPending}
                                      className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
                                    >
                                      Skip
                                    </button>
                                  </>
                                )}
                                {step.status === "sent" && (
                                  <button
                                    data-testid={`button-undo-sent-${idx}`}
                                    onClick={() => selectedLead && updateStepStatusMutation.mutate({ leadId: selectedLead.id, stepIndex: idx, status: "upcoming" })}
                                    disabled={updateStepStatusMutation.isPending}
                                    className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 border border-green-500/20"
                                  >
                                    Sent
                                  </button>
                                )}
                                {step.status === "skipped" && (
                                  <button
                                    data-testid={`button-undo-skipped-${idx}`}
                                    onClick={() => selectedLead && updateStepStatusMutation.mutate({ leadId: selectedLead.id, stepIndex: idx, status: "upcoming" })}
                                    disabled={updateStepStatusMutation.isPending}
                                    className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20"
                                  >
                                    Skipped
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-3" data-testid="nurture-plan-empty">
                      <p className="text-[11px] text-muted-foreground/50">No nurture plan generated yet.</p>
                    </div>
                  )}
                  <button
                    data-testid="button-generate-sequence"
                    onClick={() => selectedLead && generateSequenceMutation.mutate(selectedLead.id)}
                    disabled={generateSequenceMutation.isPending}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600/15 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-600/25 text-xs font-bold transition-colors disabled:opacity-40"
                  >
                    {generateSequenceMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                    {generateSequenceMutation.isPending ? "Generating..." : selectedLead.nurtureSequence ? "Regenerate Plan" : "Generate Nurture Plan"}
                  </button>
                </div>
              )}
            </div>

            {/* T002: Lead Memory Panel */}
            <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-3" data-testid="memory-panel">
              <button
                onClick={() => setMemoryPanelOpen(!memoryPanelOpen)}
                className="w-full flex items-center justify-between"
                data-testid="button-toggle-memory-panel"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Interaction Memory</span>
                  {leadMemories.length > 0 && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">{leadMemories.length}</span>
                  )}
                </div>
                {memoryPanelOpen ? <ChevronUp size={14} className="text-muted-foreground/50" /> : <ChevronDown size={14} className="text-muted-foreground/50" />}
              </button>
              {memoryPanelOpen && (
                <div className="space-y-2">
                  {leadMemories.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {leadMemories.map((m: any, i: number) => {
                        const sourceIcons: Record<string, string> = {
                          email_sent: "→",
                          email_received: "←",
                          chat: "💬",
                          field_update: "✏",
                          note: "📝",
                        };
                        const icon = sourceIcons[m.source] || "•";
                        const ts = m.createdAt ? new Date(m.createdAt).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "";
                        return (
                          <div key={m.id || i} className="flex gap-2.5 items-start" data-testid={`memory-item-${i}`}>
                            <span className="mt-0.5 text-[11px] text-blue-400/70 shrink-0 w-4 text-center" title={m.source?.replace(/_/g, " ")}>{icon}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-foreground/80 leading-relaxed">{m.summary}</p>
                              <p className="text-[10px] text-muted-foreground/50 mt-0.5">
                                {m.source && <span className="capitalize">{m.source.replace(/_/g, " ")} · </span>}
                                {ts}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-[11px] text-muted-foreground/50 py-1" data-testid="memory-panel-empty">No memories yet. Rogers builds context as you interact with this lead.</p>
                  )}
                </div>
              )}
            </div>

            {/* Sparse lead enrichment prompt */}
            {(() => {
              const cf = (selectedLead as any).customFields as Record<string, string> | null;
              const hasProspectData = cf && (cf._prospect_industry || cf._prospect_company || cf._prospect_pain_points || cf._prospect_brief);
              const hasNotes = selectedLead.notes && Array.isArray(selectedLead.notes) && (selectedLead.notes as string[]).some((n: string) => n?.trim().length > 0);
              const isSparseLead = !hasProspectData && !hasNotes;
              if (!isSparseLead) return null;
              return (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4 space-y-3" data-testid="sparse-lead-enrichment">
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-0.5">
                      <h3 className="text-xs font-bold text-amber-400 flex items-center gap-1.5">
                        <Sparkles size={12} />
                        Improve Rogers' Personalization
                      </h3>
                      <p className="text-[11px] text-muted-foreground/70">Add more context so Rogers can send more relevant messages to {selectedLead.name}.</p>
                    </div>
                    <button
                      data-testid="button-toggle-enrich-form"
                      onClick={() => setShowEnrichForm(!showEnrichForm)}
                      className="text-[10px] font-bold text-amber-400 border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 rounded-lg px-2 py-1 transition-colors shrink-0"
                    >
                      {showEnrichForm ? "Cancel" : "Add Details"}
                    </button>
                  </div>
                  {showEnrichForm && (
                    <div className="space-y-2 pt-1" data-testid="enrich-form">
                      <input
                        data-testid="input-enrich-company"
                        value={enrichCompany}
                        onChange={(e) => setEnrichCompany(e.target.value)}
                        placeholder="Company name"
                        className="w-full bg-muted/50 border border-border focus:border-amber-500/50 outline-none rounded-xl px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60"
                      />
                      <input
                        data-testid="input-enrich-industry"
                        value={enrichIndustry}
                        onChange={(e) => setEnrichIndustry(e.target.value)}
                        placeholder="Industry (e.g. Real estate, SaaS, Legal)"
                        className="w-full bg-muted/50 border border-border focus:border-amber-500/50 outline-none rounded-xl px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60"
                      />
                      <textarea
                        data-testid="textarea-enrich-pain-points"
                        value={enrichPainPoints}
                        onChange={(e) => setEnrichPainPoints(e.target.value)}
                        placeholder="Pain points or goals (e.g. needs more leads, struggling with retention)"
                        rows={2}
                        className="w-full bg-muted/50 border border-border focus:border-amber-500/50 outline-none rounded-xl px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 resize-none"
                      />
                      <button
                        data-testid="button-save-enrich"
                        onClick={() => {
                          if (!selectedLead) return;
                          enrichLeadMutation.mutate({
                            leadId: selectedLead.id,
                            industry: enrichIndustry.trim(),
                            company: enrichCompany.trim(),
                            painPoints: enrichPainPoints.trim(),
                          });
                        }}
                        disabled={(!enrichIndustry.trim() && !enrichCompany.trim() && !enrichPainPoints.trim()) || enrichLeadMutation.isPending}
                        className="flex items-center gap-2 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-400 font-bold rounded-xl px-3 py-2 text-xs transition-colors disabled:opacity-40"
                      >
                        {enrichLeadMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                        Save Context
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="flex flex-col gap-2">
              <button
                data-testid="button-book-appointment"
                onClick={() => { setShowAppointmentForm(!showAppointmentForm); setShowEmailForm(false); }}
                className="bg-card/50 border border-border hover:border-border text-foreground font-bold rounded-xl px-4 py-3 text-sm flex items-center gap-2 transition-colors w-full justify-center"
              >
                <Calendar size={14} />
                Book Appointment
              </button>
              <button
                data-testid="button-queue-email-draft"
                onClick={() => { setShowEmailForm(!showEmailForm); setShowAppointmentForm(false); }}
                className="bg-card/50 border border-border hover:border-border text-foreground font-bold rounded-xl px-4 py-3 text-sm flex items-center gap-2 transition-colors w-full justify-center"
              >
                <Mail size={14} />
                Queue Email Draft
              </button>
            </div>

            {showAppointmentForm && (
              <div className="bg-card/50 border border-border rounded-2xl p-5 space-y-3" data-testid="appointment-form">
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">New Appointment</h3>
                <input
                  data-testid="input-appt-title"
                  value={apptTitle}
                  onChange={(e) => setApptTitle(e.target.value)}
                  placeholder="Appointment title"
                  className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70"
                />
                <div className="space-y-1">
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">Start</label>
                  <input
                    data-testid="input-appt-start"
                    type="datetime-local"
                    value={apptStartAt}
                    onChange={(e) => setApptStartAt(e.target.value)}
                    className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-2.5 text-sm text-foreground [color-scheme:dark]"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">End</label>
                  <input
                    data-testid="input-appt-end"
                    type="datetime-local"
                    value={apptEndAt}
                    onChange={(e) => setApptEndAt(e.target.value)}
                    className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-2.5 text-sm text-foreground [color-scheme:dark]"
                  />
                </div>
                <input
                  data-testid="input-appt-link"
                  value={apptMeetingLink}
                  onChange={(e) => setApptMeetingLink(e.target.value)}
                  placeholder="Meeting link (optional)"
                  className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70"
                />
                <div className="flex gap-2">
                  <button
                    data-testid="button-confirm-appointment"
                    onClick={handleBookAppointment}
                    disabled={!apptTitle.trim() || !apptStartAt || !apptEndAt || createAppointmentMutation.isPending}
                    className="bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-bold rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 transition-colors flex-1 justify-center"
                  >
                    {createAppointmentMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Calendar size={14} />}
                    Book
                  </button>
                  <button
                    data-testid="button-cancel-appointment"
                    onClick={() => setShowAppointmentForm(false)}
                    className="text-muted-foreground hover:text-foreground font-bold rounded-xl px-4 py-2.5 text-sm transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {showEmailForm && (
              <div className="bg-card/50 border border-border rounded-2xl p-5 space-y-3" data-testid="email-form">
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Draft Nurture Email</h3>
                <p className="text-[10px] text-muted-foreground/60" data-testid="text-email-draft-note">Email drafts are saved to your outbox. Review and manage them in Settings.</p>
                <textarea
                  data-testid="input-email-prompt"
                  value={emailPrompt}
                  onChange={(e) => setEmailPrompt(e.target.value)}
                  placeholder="Optional: provide a prompt for the email content..."
                  rows={3}
                  className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 resize-none"
                />
                <div className="flex gap-2">
                  <button
                    data-testid="button-confirm-email"
                    onClick={() => selectedLead && sendEmailMutation.mutate({ leadId: selectedLead.id, prompt: emailPrompt.trim() || undefined })}
                    disabled={sendEmailMutation.isPending}
                    className="bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-bold rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 transition-colors flex-1 justify-center"
                  >
                    {sendEmailMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                    Queue Draft
                  </button>
                  <button
                    data-testid="button-cancel-email"
                    onClick={() => setShowEmailForm(false)}
                    className="text-muted-foreground hover:text-foreground font-bold rounded-xl px-4 py-2.5 text-sm transition-colors"
                  >
                    Cancel
                  </button>
                </div>
                {sendEmailMutation.isSuccess && (
                  <p className="text-green-400 text-xs" data-testid="email-success">Email draft saved to outbox.</p>
                )}
                {sendEmailMutation.isError && (
                  <p className="text-red-400 text-xs" data-testid="email-error">Failed to queue email draft. Please try again.</p>
                )}
              </div>
            )}

            {leadAppointments.length > 0 && (
              <div className="bg-card/50 border border-border rounded-2xl p-5 space-y-3" data-testid="lead-appointments">
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Appointments</h3>
                <div className="space-y-2">
                  {leadAppointments.map((appt) => (
                    <div key={appt.id} className="bg-muted/50 rounded-xl px-3 py-2.5 space-y-1" data-testid={`appointment-${appt.id}`}>
                      <div className="flex items-center gap-2">
                        <Calendar size={12} className="text-blue-400 shrink-0" />
                        <span className="text-sm font-medium text-foreground">{appt.title}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        {new Date(appt.startAt).toLocaleString()} - {new Date(appt.endAt).toLocaleTimeString()}
                      </p>
                      {appt.meetingLink && (
                        <div className="flex items-center gap-1 text-[10px] text-blue-400">
                          <LinkIcon size={10} />
                          <a href={appt.meetingLink} target="_blank" rel="noreferrer" className="underline" data-testid={`link-meeting-${appt.id}`}>
                            Join Meeting
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-2 bg-card/50 border border-border rounded-2xl p-4 md:p-6 flex flex-col" data-testid="nurture-chat">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Email Thread — Rogers</h3>
              {leadEmails.length > 0 && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {leadEmails.filter((e: any) => e.status === "SENT").length} sent · {leadEmails.filter((e: any) => e.status === "QUEUED").length} queued
                </span>
              )}
            </div>
            <div className="flex-1 space-y-2 max-h-[28rem] overflow-y-auto mb-4 pr-1">
              {messagesLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={20} className="animate-spin text-muted-foreground/70" />
                </div>
              )}
              {!messagesLoading && leadEmails.length === 0 && messages.filter((m: any) => m.channel === "email").length === 0 && (
                <div className="flex flex-col gap-4 py-4">
                  <div className="text-center space-y-2">
                    <Mail size={28} className="text-muted-foreground/20 mx-auto" />
                    <p className="text-muted-foreground/50 text-sm">No emails yet. Ask Rogers to reach out or use the starters below.</p>
                  </div>
                  <div className="space-y-2 flex flex-col">
                    {suggestedStarters.map((starter, i) => (
                      <button
                        key={i}
                        data-testid={`button-suggested-starter-${i}`}
                        onClick={() => {
                          nurtureMutation.mutate({ leadId: selectedLead.id, message: starter });
                        }}
                        disabled={nurtureMutation.isPending}
                        className="text-left text-xs bg-muted/50 border border-border hover:border-foreground/30 rounded-lg px-3 py-2 text-foreground/70 hover:text-foreground transition-colors disabled:opacity-40"
                      >
                        "{starter}"
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Unified email thread: outbox emails (Rogers sent) + lead reply messages */}
              {(() => {
                const outboxItems = (leadEmails || []).map((e: any) => ({
                  id: `outbox-${e.id}`,
                  rawId: e.id,
                  type: "outbound" as const,
                  subject: e.subject,
                  body: e.body,
                  status: e.status,
                  timestamp: e.sentAt || e.createdAt,
                  error: e.error,
                }));
                const replyItems = (messages || [])
                  .filter((m: any) => m.role === "user" && m.channel === "email")
                  .map((m: any) => ({
                    id: `reply-${m.id}`,
                    type: "inbound" as const,
                    subject: null,
                    body: m.content,
                    status: "RECEIVED",
                    timestamp: m.createdAt,
                    error: null,
                  }));
                const chatItems = (messages || [])
                  .filter((m: any) => m.channel !== "email")
                  .map((m: any) => ({
                    id: `chat-${m.id}`,
                    type: m.role === "user" ? "chat-lead" as const : "chat-rogers" as const,
                    subject: null,
                    body: m.content,
                    status: "NOTE",
                    timestamp: m.createdAt,
                    error: null,
                  }));
                const allItems = [...outboxItems, ...replyItems, ...chatItems]
                  .sort((a, b) => new Date(a.timestamp || 0).getTime() - new Date(b.timestamp || 0).getTime());
                if (allItems.length === 0) return null;
                return allItems.map((item) => {
                  const isExpanded = expandedEmailIds.has(item.id);
                  const toggleExpand = () => setExpandedEmailIds(prev => {
                    const next = new Set(prev);
                    if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
                    return next;
                  });
                  const statusColor = item.status === "SENT" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
                    : item.status === "QUEUED" ? "text-amber-400 bg-amber-500/10 border-amber-500/20"
                    : item.status === "FAILED" ? "text-red-400 bg-red-500/10 border-red-500/20"
                    : item.status === "RECEIVED" ? "text-blue-400 bg-blue-500/10 border-blue-500/20"
                    : "text-muted-foreground bg-muted/30 border-border";
                  if (item.type === "chat-rogers" || item.type === "chat-lead") {
                    return (
                      <div key={item.id} className={`flex flex-col gap-1 max-w-[80%] ${item.type === "chat-lead" ? "ml-auto items-end" : "items-start"}`} data-testid={`chat-message-${item.id}`}>
                        <span className={`text-[10px] font-semibold uppercase tracking-wide ${item.type === "chat-lead" ? "text-blue-400" : "text-emerald-400"}`}>
                          {item.type === "chat-lead" ? "You (Note)" : "Rogers (Note)"}
                        </span>
                        <div className={`rounded-xl px-3 py-2 text-xs ${item.type === "chat-lead" ? "bg-blue-600/10 text-blue-200" : "bg-muted/50 text-foreground/70"}`}>
                          {item.body}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={item.id}
                      data-testid={`email-thread-item-${item.id}`}
                      className={`rounded-xl border p-3 space-y-1.5 ${
                        item.type === "inbound"
                          ? "bg-blue-500/5 border-blue-500/20"
                          : "bg-card/80 border-border"
                      }`}
                    >
                      <div className="flex items-start gap-2 justify-between">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase shrink-0 ${statusColor}`}>
                            {item.status === "RECEIVED" ? "Lead Reply" : item.status === "NOTE" ? "Note" : item.status}
                          </span>
                          {item.subject && (
                            <span className="text-xs font-semibold text-foreground/90 truncate">{item.subject}</span>
                          )}
                        </div>
                        <span className="text-[9px] text-muted-foreground/50 shrink-0 font-mono">
                          {item.timestamp ? new Date(item.timestamp).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                        </span>
                      </div>
                      <div className="text-xs text-foreground/70 leading-relaxed">
                        {isExpanded
                          ? item.body
                          : (item.body || "").slice(0, 160) + ((item.body || "").length > 160 ? "…" : "")}
                      </div>
                      {(item.body || "").length > 160 && (
                        <button onClick={toggleExpand} className="text-[10px] text-blue-400 hover:text-blue-300 font-semibold" data-testid={`btn-expand-${item.id}`}>
                          {isExpanded ? "Show less" : "Show full email"}
                        </button>
                      )}
                      {item.error && (
                        <p className="text-[10px] text-red-400">Error: {item.error}</p>
                      )}
                      {item.status === "QUEUED" && item.type === "outbound" && (item as any).rawId && (
                        <div className="flex items-center gap-2 pt-1 border-t border-amber-500/20" data-testid={`approval-actions-${item.id}`}>
                          <span className="text-[9px] text-amber-400/80 font-semibold flex-1">Awaiting approval</span>
                          <button
                            data-testid={`button-approve-email-${(item as any).rawId}`}
                            onClick={() => activeWorkspaceId && approveEmailMutation.mutate({ wsId: activeWorkspaceId, emailId: (item as any).rawId })}
                            disabled={approveEmailMutation.isPending || discardEmailMutation.isPending}
                            className="flex items-center gap-1 text-[9px] font-bold px-2 py-1 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors"
                          >
                            {approveEmailMutation.isPending ? <Loader2 size={9} className="animate-spin" /> : <Check size={9} />}
                            Approve & Send
                          </button>
                          <button
                            data-testid={`button-discard-email-${(item as any).rawId}`}
                            onClick={() => activeWorkspaceId && discardEmailMutation.mutate({ wsId: activeWorkspaceId, emailId: (item as any).rawId })}
                            disabled={approveEmailMutation.isPending || discardEmailMutation.isPending}
                            className="flex items-center gap-1 text-[9px] font-bold px-2 py-1 rounded bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
                          >
                            {discardEmailMutation.isPending ? <Loader2 size={9} className="animate-spin" /> : null}
                            Discard
                          </button>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
              {nurtureMutation.isPending && (
                <div className="flex items-center gap-2 text-muted-foreground/70 text-sm">
                  <span className="inline-flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-foreground/30 animate-bounce" style={{ animationDelay: "0ms" }}></span>
                    <span className="w-2 h-2 rounded-full bg-foreground/30 animate-bounce" style={{ animationDelay: "150ms" }}></span>
                    <span className="w-2 h-2 rounded-full bg-foreground/30 animate-bounce" style={{ animationDelay: "300ms" }}></span>
                  </span>
                  Rogers is composing...
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <div className="space-y-2">
              <div className="flex gap-1 bg-muted/30 border border-border rounded-xl p-1 w-fit">
                <button
                  data-testid="tab-nurture-mode"
                  onClick={() => setReplyMode("nurture")}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${replyMode === "nurture" ? "bg-blue-600 text-white" : "text-muted-foreground hover:text-foreground"}`}
                >
                  Ask Rogers
                </button>
                <button
                  data-testid="tab-reply-mode"
                  onClick={() => setReplyMode("reply")}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${replyMode === "reply" ? "bg-emerald-600 text-white" : "text-muted-foreground hover:text-foreground"}`}
                  title="Log an inbound reply from this lead to trigger Rogers' response"
                >
                  Log Reply
                </button>
              </div>

              {replyMode === "nurture" ? (
                <div className="flex gap-2">
                  <input
                    data-testid="input-nurture"
                    value={nurtureInput}
                    onChange={(e) => setNurtureInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleNurtureSend()}
                    placeholder="Ask Rogers for nurture suggestions..."
                    className="flex-1 bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70"
                  />
                  <button
                    data-testid="button-send-nurture"
                    onClick={handleNurtureSend}
                    disabled={!nurtureInput.trim() || nurtureMutation.isPending}
                    className="bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-bold rounded-xl px-4 py-3 transition-colors"
                  >
                    <Send size={16} />
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      data-testid="input-lead-reply"
                      value={replyInput}
                      onChange={(e) => setReplyInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleReplySend()}
                      placeholder="Paste or type the lead's reply message..."
                      className="flex-1 bg-muted/50 border border-emerald-500/40 focus:border-emerald-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70"
                    />
                    <button
                      data-testid="button-send-reply"
                      onClick={handleReplySend}
                      disabled={!replyInput.trim() || replyMutation.isPending}
                      className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-bold rounded-xl px-4 py-3 transition-colors"
                    >
                      {replyMutation.isPending ? (
                        <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin block" />
                      ) : (
                        <Send size={16} />
                      )}
                    </button>
                  </div>
                  {lastReplyIntent && lastReplyIntent !== "NEUTRAL" && (
                    <div data-testid="intent-badge" className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg w-fit font-semibold ${lastReplyIntent === "CLOSE_SIGNAL" ? "bg-emerald-600/20 text-emerald-400 border border-emerald-500/30" : lastReplyIntent === "PRICE_CONCERN" ? "bg-orange-600/20 text-orange-400 border border-orange-500/30" : "bg-blue-600/20 text-blue-400 border border-blue-500/30"}`}>
                      {lastReplyIntent === "CLOSE_SIGNAL" ? "🎯" : lastReplyIntent === "PRICE_CONCERN" ? "💰" : lastReplyIntent === "COMPETITOR_COMPARISON" ? "⚔️" : "💡"} Intent: {lastReplyIntent.replace(/_/g, " ")}
                      {lastBookingUrl && <span className="ml-1 text-emerald-300">· Booking created!</span>}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground/60">Rogers will detect the lead's intent and craft a targeted response automatically.</p>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-3">
            <RogersVoice
              onTranscript={(text) => {
                setNurtureInput(text);
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  function formatRelativeTime(dateStr: string) {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  }

  function navigateToLeadFromInbox(leadId: number) {
    const lead = leads?.find((l) => l.id === leadId);
    if (lead) {
      setActiveTab("leads");
      openLeadDetail(lead);
    }
  }

  return (
    <div className="space-y-8" data-testid="lead-desk-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">Lead Desk</h1>
          <p className="text-muted-foreground mt-1 text-sm">Agent Rogers — Manage and nurture your leads.</p>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto overflow-x-auto">
          <div className="flex bg-card/50 border border-border rounded-xl overflow-hidden shrink-0">
            {([
              { id: "leads" as LeadDeskTab, label: "Leads", icon: MessageSquare },
              { id: "funnel" as LeadDeskTab, label: "Funnel", icon: TrendingUp },
              { id: "inbox" as LeadDeskTab, label: "Inbox", icon: Inbox },
              { id: "appointments" as LeadDeskTab, label: "Appointments", icon: Calendar },
              { id: "analytics" as LeadDeskTab, label: "Analytics", icon: BarChart3 },
              { id: "email" as LeadDeskTab, label: "Email", icon: Mail },
              { id: "nurture" as LeadDeskTab, label: "Nurture", icon: Zap },
              { id: "site-agent" as LeadDeskTab, label: "Site Agent", icon: MessageSquare },
            ]).map((tab) => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => { setActiveTab(tab.id); if (tab.id === "leads") setSelectedLead(null); }}
                className={`flex items-center gap-1.5 px-3 md:px-4 py-2 text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-blue-600/20 text-blue-400"
                    : "text-muted-foreground hover:text-muted-foreground"
                }`}
              >
                <tab.icon size={14} />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.slice(0, 4)}</span>
              </button>
            ))}
          </div>
          {activeTab === "leads" && (
            <div className="flex items-center gap-2">
              <button
                data-testid="button-export-leads-csv"
                onClick={() => {
                  const url = `/api/workspaces/${activeWorkspaceId}/leads/export/csv`;
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "leads.csv";
                  a.click();
                }}
                className="border border-border hover:border-border/80 text-muted-foreground hover:text-foreground font-bold rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 transition-colors"
              >
                <Download size={14} />
                Export CSV
              </button>
              <button
                data-testid="button-add-lead"
                onClick={() => setShowForm(!showForm)}
                className="bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl px-5 py-2.5 text-sm flex items-center gap-2 transition-colors"
              >
                <UserPlus size={16} />
                Add Lead
              </button>
            </div>
          )}
        </div>
      </div>

      {activeTab === "inbox" && (
        <div data-testid="inbox-tab" className="space-y-4">
          {/* Reply submission panel */}
          <div className="bg-card/50 border border-border rounded-2xl p-5 space-y-3" data-testid="inbox-reply-panel">
            <div className="flex items-start gap-2">
              <div className="flex-1 space-y-1">
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                  <Mail size={12} className="text-emerald-400" />
                  Submit Lead Reply
                </h3>
                <p className="text-[11px] text-muted-foreground/60">Rogers processes this reply and sends an AI response automatically.</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex gap-2">
                <select
                  data-testid="select-inbox-reply-lead"
                  value={inboxReplyLeadId ?? ""}
                  onChange={(e) => setInboxReplyLeadId(e.target.value ? Number(e.target.value) : null)}
                  className="bg-muted/50 border border-border focus:border-emerald-500/60 outline-none rounded-xl px-3 py-2 text-sm text-foreground w-48 shrink-0"
                >
                  <option value="">Select lead...</option>
                  {(leads || []).filter(l => l.status !== "OPTED_OUT" && l.status !== "LOST").map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
              <textarea
                data-testid="textarea-inbox-reply"
                value={inboxReplyText}
                onChange={(e) => setInboxReplyText(e.target.value)}
                placeholder="Paste inbound email reply from your lead here..."
                rows={3}
                className="w-full bg-muted/50 border border-border focus:border-emerald-500/60 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 resize-none"
              />
              <button
                data-testid="button-process-reply"
                onClick={() => {
                  if (!inboxReplyLeadId || !inboxReplyText.trim()) return;
                  inboxReplyMutation.mutate({ leadId: inboxReplyLeadId, content: inboxReplyText.trim() });
                }}
                disabled={!inboxReplyLeadId || !inboxReplyText.trim() || inboxReplyMutation.isPending}
                className="flex items-center gap-2 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-400 font-bold rounded-xl px-4 py-2 text-sm transition-colors disabled:opacity-40"
              >
                {inboxReplyMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Process Reply
              </button>
            </div>
          </div>

          {/* Message list */}
          {inboxLoading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-muted-foreground/50" size={24} /></div>
          ) : unifiedMessages && unifiedMessages.length > 0 ? (
            <div className="space-y-1" data-testid="inbox-messages">
              {unifiedMessages.map((msg: any) => {
                const isOutbox = msg.itemType === "outbox";
                const statusBadge = isOutbox && msg.emailStatus ? (
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${
                    msg.emailStatus === "SENT" ? "bg-emerald-500/10 text-emerald-400" :
                    msg.emailStatus === "FAILED" ? "bg-red-500/10 text-red-400" :
                    "bg-amber-500/10 text-amber-400"
                  }`} data-testid={`badge-email-status-${msg.messageId}`}>
                    {msg.emailStatus}
                  </span>
                ) : null;
                return (
                  <div
                    key={msg.messageId}
                    data-testid={`inbox-msg-${msg.messageId}`}
                    onClick={() => navigateToLeadFromInbox(msg.leadId)}
                    className="bg-muted/30 border border-border rounded-xl px-5 py-3.5 flex items-center gap-4 cursor-pointer hover:bg-muted/50 hover:border-border transition-colors"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      isOutbox ? "bg-gradient-to-br from-emerald-500/20 to-blue-500/20 text-emerald-400" : "bg-gradient-to-br from-blue-500/20 to-purple-500/20 text-muted-foreground"
                    }`}>
                      {isOutbox ? <Mail size={14} /> : (msg.leadName?.[0] || "?").toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-semibold text-foreground/90">{msg.leadName}</span>
                        {isOutbox ? (
                          <>
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase bg-emerald-500/10 text-emerald-400">Rogers</span>
                            {statusBadge}
                          </>
                        ) : (
                          <>
                            {msg.role === "user" && msg.channel === "email" ? (
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase bg-blue-500/10 text-blue-400" data-testid={`badge-reply-${msg.messageId}`}>
                                Reply
                              </span>
                            ) : (
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${
                                msg.role === "user" ? "bg-blue-500/10 text-blue-400" : "bg-emerald-500/10 text-emerald-400"
                              }`}>
                                {msg.role === "user" ? "Lead" : "AI"}
                              </span>
                            )}
                            {msg.channel !== "chat" && msg.channel !== "email" && (
                              <span className="text-[9px] text-muted-foreground/50 uppercase">{msg.channel}</span>
                            )}
                          </>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground/70 truncate">
                        {isOutbox && msg.subject ? (
                          <span className="font-medium">{msg.subject}</span>
                        ) : (
                          <>{msg.content?.slice(0, 60)}{msg.content?.length > 60 ? "..." : ""}</>
                        )}
                      </p>
                    </div>
                    <span className="text-[10px] text-muted-foreground/50 font-mono shrink-0">{formatRelativeTime(msg.createdAt)}</span>
                    <ArrowLeft size={14} className="text-muted-foreground/40 rotate-180 shrink-0" />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-card/50 border border-border rounded-2xl p-12 text-center" data-testid="inbox-empty">
              <Inbox size={40} className="text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground/70 text-sm">No messages yet. Start conversations with your leads.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === "appointments" && (
        <Suspense fallback={<div className="flex items-center justify-center py-20"><span className="text-muted-foreground/50">Loading...</span></div>}>
          <AppointmentsTab workspaceId={activeWorkspaceId} bookingSlug={currentWorkspace?.bookingSlug} />
        </Suspense>
      )}

      {activeTab === "funnel" && (
        <Suspense fallback={<div className="flex items-center justify-center py-20"><span className="text-muted-foreground/50">Loading...</span></div>}>
          <FunnelTab leads={leads || []} isLoading={isLoading} onSelectLead={openLeadDetail} />
        </Suspense>
      )}

      {activeTab === "analytics" && (
        <Suspense fallback={<div className="flex items-center justify-center py-20"><span className="text-muted-foreground/50 animate-spin">loading...</span></div>}>
          <AnalyticsTab leads={leads} isLoading={isLoading} statusCounts={statusCounts} />
        </Suspense>
      )}

      {activeTab === "email" && (
        <div className="space-y-6" data-testid="email-tab">
          <Suspense fallback={<div className="flex items-center justify-center py-8"><span className="text-muted-foreground/50">Loading...</span></div>}>
            <EmailTemplateManager wsId={activeWorkspaceId} />
          </Suspense>
          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          <EmailMarketing />
        </div>
      )}

      {activeTab === "nurture" && (
        <div className="space-y-6 max-w-2xl mx-auto" data-testid="nurture-control-panel">
          <div className="bg-card/50 border border-border rounded-2xl p-6 space-y-5">
            <div className="flex items-center gap-2">
              <Zap size={16} className="text-amber-400" />
              <h2 className="text-sm font-bold text-foreground">Nurture Control Panel</h2>
            </div>
            <p className="text-xs text-muted-foreground/70">
              Configure workspace-wide defaults for Rogers' automated outreach. These settings apply to all leads in this workspace.
            </p>

            <div className="space-y-2">
              <label className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-bold">Default Cadence (Days Between Emails)</label>
              <div className="flex flex-wrap gap-1.5">
                {[1, 2, 3, 5, 7].map((days) => (
                  <button
                    key={days}
                    data-testid={`button-ws-cadence-${days}`}
                    onClick={() => updateNurtureSettingsMutation.mutate({ defaultCadenceDays: days })}
                    disabled={updateNurtureSettingsMutation.isPending}
                    className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors border ${
                      nurtureSettings?.defaultCadenceDays === days
                        ? "bg-blue-600/20 text-blue-400 border-blue-500/30"
                        : "bg-muted/50 text-muted-foreground/70 border-border hover:text-foreground hover:border-border"
                    }`}
                  >
                    {days}d
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-bold">Email Tone Preference</label>
              <div className="flex flex-wrap gap-1.5">
                {([
                  { value: "professional", label: "Professional" },
                  { value: "friendly", label: "Friendly" },
                  { value: "urgent", label: "Urgent" },
                ] as const).map(({ value, label }) => (
                  <button
                    key={value}
                    data-testid={`button-ws-tone-${value}`}
                    onClick={() => updateNurtureSettingsMutation.mutate({ emailTonePreference: value })}
                    disabled={updateNurtureSettingsMutation.isPending}
                    className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors border ${
                      nurtureSettings?.emailTonePreference === value
                        ? "bg-purple-600/20 text-purple-400 border-purple-500/30"
                        : "bg-muted/50 text-muted-foreground/70 border-border hover:text-foreground hover:border-border"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-bold">Approval Queue</label>
              <div className="flex items-center gap-3 bg-muted/30 border border-border rounded-xl px-4 py-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">Require approval before sending</p>
                  <p className="text-xs text-muted-foreground/70 mt-0.5">
                    When enabled, Rogers queues emails for your review. You approve or discard from each lead's email thread.
                  </p>
                </div>
                <button
                  data-testid="button-toggle-requires-approval"
                  onClick={() => updateNurtureSettingsMutation.mutate({ requiresApproval: !nurtureSettings?.requiresApproval })}
                  disabled={updateNurtureSettingsMutation.isPending}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none shrink-0 ${
                    nurtureSettings?.requiresApproval ? "bg-amber-500" : "bg-muted/60 border border-border"
                  }`}
                  role="switch"
                  aria-checked={nurtureSettings?.requiresApproval}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      nurtureSettings?.requiresApproval ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
              {nurtureSettings?.requiresApproval && (
                <p className="text-[10px] text-amber-400/80 bg-amber-500/8 border border-amber-500/15 rounded-lg px-3 py-1.5" data-testid="approval-mode-notice">
                  Approval mode is active. Rogers will queue all outbound emails for your review before sending.
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-border">
              {updateNurtureSettingsMutation.isPending && (
                <Loader2 size={12} className="animate-spin text-muted-foreground/60" />
              )}
              <span className="text-[10px] text-muted-foreground/50">
                {updateNurtureSettingsMutation.isPending ? "Saving…" : "Changes are saved automatically."}
              </span>
            </div>
          </div>
        </div>
      )}

      {activeTab === "site-agent" && (
        <Suspense fallback={<div className="flex items-center justify-center py-20"><span className="text-muted-foreground/50">Loading...</span></div>}>
          <SiteAgentConversations wsId={activeWorkspaceId} />
        </Suspense>
      )}

      {activeTab === "leads" && (<>

      {leadsError && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 flex items-start gap-3" data-testid="leads-error-banner">
          <div className="text-red-400 mt-0.5">⚠</div>
          <div>
            <p className="text-sm font-bold text-red-400">Unable to load leads</p>
            <p className="text-xs text-muted-foreground/70 mt-1">You may not have permission to view leads in this workspace. Contact the workspace owner to request access.</p>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card/50 overflow-hidden" data-testid="lead-capture-card">
        <button
          data-testid="button-toggle-capture-card"
          onClick={() => setShowCaptureCard(!showCaptureCard)}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/10">
              <Webhook size={16} className="text-orange-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">Lead Capture</p>
              <p className="text-xs text-muted-foreground/70">Capture leads from any website or form</p>
            </div>
          </div>
          {showCaptureCard ? <ChevronUp size={16} className="text-muted-foreground/60" /> : <ChevronDown size={16} className="text-muted-foreground/60" />}
        </button>
        {showCaptureCard && (
          <div className="px-4 pb-4 space-y-4 border-t border-border pt-4">
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Endpoint URL</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-[11px] bg-muted/50 border border-border rounded-lg px-3 py-2 text-foreground/80 font-mono truncate" data-testid="text-capture-url">
                  {window.location.origin}/api/public/lead-capture
                </code>
                <button
                  data-testid="button-copy-capture-url"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/api/public/lead-capture`);
                    setCaptureUrlCopied(true);
                    setTimeout(() => setCaptureUrlCopied(false), 2000);
                  }}
                  className="p-2 rounded-lg bg-muted/50 border border-border text-muted-foreground hover:text-foreground transition-colors shrink-0"
                >
                  {captureUrlCopied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <Key size={10} /> API Key
              </label>
              {captureApiKey ? (
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[11px] bg-muted/50 border border-border rounded-lg px-3 py-2 text-foreground/80 font-mono truncate" data-testid="text-capture-api-key">
                    {showCaptureKey ? captureApiKey : `••••${captureApiKey.slice(-4)}`}
                  </code>
                  <button
                    data-testid="button-toggle-show-key"
                    onClick={() => setShowCaptureKey(!showCaptureKey)}
                    className="p-2 rounded-lg bg-muted/50 border border-border text-muted-foreground hover:text-foreground transition-colors shrink-0 text-[10px] font-bold"
                  >
                    {showCaptureKey ? "Hide" : "Show"}
                  </button>
                  <button
                    data-testid="button-copy-capture-key"
                    onClick={() => {
                      navigator.clipboard.writeText(captureApiKey);
                      setCaptureKeyCopied(true);
                      setTimeout(() => setCaptureKeyCopied(false), 2000);
                    }}
                    className="p-2 rounded-lg bg-muted/50 border border-border text-muted-foreground hover:text-foreground transition-colors shrink-0"
                  >
                    {captureKeyCopied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                  </button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground/60">No API key yet. <a href="/settings" className="text-orange-400 underline">Go to Settings → Lead Capture API</a> to generate one.</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                data-testid="button-send-test-lead"
                disabled={testingLead || !captureApiKey}
                onClick={async () => {
                  if (!captureApiKey) return;
                  setTestingLead(true);
                  try {
                    const res = await fetch("/api/public/lead-capture", {
                      method: "POST",
                      headers: { "Content-Type": "application/json", "X-API-Key": captureApiKey },
                      body: JSON.stringify({ name: "Test Lead", email: `test-${Date.now()}@drpr.ai`, source: "Test Button" }),
                    });
                    if (res.ok) {
                      toast({ title: "Test lead sent!", description: "Rogers will score and welcome this lead." });
                      qc.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "leads"] });
                    } else {
                      const errData = await res.json().catch(() => ({}));
                      toast({ title: "Test failed", description: errData.message || `HTTP ${res.status}`, variant: "destructive" });
                    }
                  } catch (e: any) {
                    toast({ title: "Network error", description: e.message, variant: "destructive" });
                  } finally {
                    setTestingLead(false);
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/20 text-orange-400 hover:bg-orange-500/20 rounded-xl text-sm font-semibold transition-colors disabled:opacity-40"
              >
                {testingLead ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                Send Test Lead
              </button>
              <a
                href="/settings"
                className="flex items-center gap-1.5 text-xs text-muted-foreground/60 hover:text-foreground transition-colors"
                data-testid="link-view-embed-code"
              >
                <ExternalLink size={12} /> View embed code
              </a>
            </div>
          </div>
        )}
      </div>

      <RogersOpsPanel wsId={activeWorkspaceId} />

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array(4)
            .fill(0)
            .map((_, i) => (
              <div key={i} className="h-24 bg-muted/50 animate-pulse rounded-2xl" />
            ))}
        </div>
      ) : (
        <div className="space-y-3">
          {/* Rogers Pipeline Summary — single card with all snapshot fields */}
          <div data-testid="rogers-pipeline-summary" className="bg-card border border-border rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Rogers Pipeline Summary</span>
              {pipelineData && (
                <span data-testid="text-pipeline-value" className="text-xs font-semibold text-primary">
                  Est. Value: ${pipelineData.estimatedPipelineValue?.toLocaleString() ?? "0"}
                </span>
              )}
            </div>
            {/* Stage breakdown */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(["NEW", "CONTACTED", "QUALIFIED", "CONVERTED"] as const).map((status) => {
                const colors = STATUS_COLORS[status];
                return (
                  <div
                    key={status}
                    data-testid={`card-pipeline-${status.toLowerCase()}`}
                    className={`${colors.bg} border ${colors.border} rounded-xl p-3 flex flex-col`}
                  >
                    <span className={`text-xs font-bold uppercase tracking-wider ${colors.text} mb-1`}>
                      {status}
                    </span>
                    <span className="text-xl font-display font-bold text-foreground">
                      {pipelineData?.byStage?.[status] ?? statusCounts[status as keyof typeof statusCounts]}
                    </span>
                  </div>
                );
              })}
            </div>
            {/* Summary row: total, overdue, hottest lead */}
            <div className="flex flex-wrap gap-4 text-sm">
              <span data-testid="text-pipeline-total" className="text-muted-foreground">
                Total: <span className="font-semibold text-foreground">{pipelineData?.total ?? leads?.length ?? 0}</span>
              </span>
              {pipelineData && pipelineData.overdueFollowUps > 0 && (
                <span data-testid="text-pipeline-overdue" className="text-amber-500 font-medium">
                  ⚠ {pipelineData.overdueFollowUps} overdue
                </span>
              )}
              {pipelineData?.hottestLead && (
                <span data-testid="text-pipeline-hottest" className="text-primary font-medium">
                  🔥 Hottest: {pipelineData.hottestLead.name} (score {pipelineData.hottestLead.score})
                </span>
              )}
            </div>
          </div>
          {pipelineData && (
            <div className="space-y-3" data-testid="rogers-war-room">
              {/* Rogers Agent Questions — hot lead war room alerts with full package */}
              {rogersAgentQuestions.map((q: any) => {
                const pkg = (() => { try { return JSON.parse(q.context || "{}"); } catch { return {}; } })();
                const dismissed = warRoomDismissedIds.has(q.id);
                if (dismissed) return null;
                const hotLeadObj = pkg.leadId ? leads?.find((x: any) => x.id === pkg.leadId) : null;
                return (
                  <div key={q.id} data-testid={`war-room-agent-question-${q.id}`} className="bg-red-600/10 border border-red-500/30 rounded-2xl overflow-hidden">
                    <div className="flex items-center justify-between gap-4 px-5 py-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-xl shrink-0">🔥</span>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-red-300">WAR ROOM — Rogers Alert</p>
                          <p className="text-xs text-red-300/70 truncate"><span className="font-semibold text-red-200">{pkg.leadName || "Hot Lead"}</span> scored {pkg.score}/100 — {pkg.urgency || "Act within 24 hours"}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {hotLeadObj && (
                          <button
                            data-testid={`button-war-room-open-lead-${q.id}`}
                            onClick={() => openLeadDetail(hotLeadObj)}
                            className="text-[10px] font-bold text-red-300 border border-red-500/40 rounded-lg px-2 py-1 bg-red-500/10 hover:bg-red-500/20 transition-colors"
                          >Act Now</button>
                        )}
                        <button
                          data-testid={`button-war-room-dismiss-${q.id}`}
                          onClick={() => setWarRoomDismissedIds(prev => new Set([...prev, q.id]))}
                          className="text-red-400/50 hover:text-red-300 text-xs px-1 py-1 transition-colors"
                          title="Dismiss"
                        >✕</button>
                      </div>
                    </div>
                    {/* Full war room package: intel brief, what they said, opener, prewritten email */}
                    <div className="border-t border-red-500/20 px-5 py-3 space-y-3">
                      {pkg.intelBrief && pkg.intelBrief !== "No brief available — run research_prospect to gather intel." && (
                        <div>
                          <p className="text-[10px] font-bold text-red-400/60 uppercase tracking-wider mb-1">Prospect Intel</p>
                          <p data-testid={`text-war-room-intel-${q.id}`} className="text-xs text-red-200/70 leading-relaxed">{pkg.intelBrief}</p>
                        </div>
                      )}
                      {pkg.whatTheySaid && pkg.whatTheySaid !== "No messages yet" && (
                        <div>
                          <p className="text-[10px] font-bold text-red-400/60 uppercase tracking-wider mb-1">What They Said</p>
                          <p data-testid={`text-war-room-said-${q.id}`} className="text-xs text-red-200/70 italic">"{pkg.whatTheySaid}"</p>
                        </div>
                      )}
                      {pkg.suggestedCallOpener && (
                        <div>
                          <p className="text-[10px] font-bold text-red-400/60 uppercase tracking-wider mb-1">Suggested Call Opener</p>
                          <p data-testid={`text-war-room-opener-${q.id}`} className="text-xs text-red-200/80 italic">"{pkg.suggestedCallOpener}"</p>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 pt-1">
                        {pkg.suggestedCallOpener && (
                          <button
                            data-testid={`button-copy-opener-${q.id}`}
                            onClick={() => navigator.clipboard.writeText(pkg.suggestedCallOpener).catch(() => {})}
                            className="text-[10px] font-bold text-red-300/80 border border-red-500/30 rounded-lg px-3 py-1.5 hover:bg-red-500/10 transition-colors"
                          >📋 Copy Opener</button>
                        )}
                        {pkg.prewroteEmailBody && hotLeadObj && (
                          <button
                            data-testid={`button-prewritten-email-${q.id}`}
                            disabled={sendPrewrittenEmailMutation.isPending}
                            onClick={() => {
                              sendPrewrittenEmailMutation.mutate({
                                leadId: hotLeadObj.id,
                                subject: pkg.prewroteEmailSubject || pkg.emailSubject || `Follow up — ${pkg.leadName}`,
                                body: pkg.prewroteEmailBody,
                              });
                            }}
                            className="text-[10px] font-bold text-red-300/80 border border-red-500/30 rounded-lg px-3 py-1.5 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                          >{sendPrewrittenEmailMutation.isPending ? "Sending…" : "✉️ Send Now"}</button>
                        )}
                        {hotLeadObj && (
                          <button
                            data-testid={`button-war-room-draft-close-${q.id}`}
                            onClick={() => { openLeadDetail(hotLeadObj); setTimeout(() => setNurtureInput(`Urgent: Write a compelling closing email for ${pkg.leadName} who scored ${pkg.score}/100. ${pkg.recommendation || ""}`), 200); }}
                            className="text-[10px] font-bold text-red-300/80 border border-red-500/30 rounded-lg px-3 py-1.5 hover:bg-red-500/10 transition-colors"
                          >🎯 Draft Close Email</button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {/* Fallback pipeline-based war room alert (when no Rogers agent questions yet) */}
              {rogersAgentQuestions.filter((q: any) => !warRoomDismissedIds.has(q.id)).length === 0 && pipelineData.hottestLead && pipelineData.hottestLead.score >= 80 && !warRoomDismissedIds.has(pipelineData.hottestLead.id) && (() => {
                const hotLead = pipelineData.hottestLead!;
                const hotLeadObj = leads?.find(x => x.id === hotLead.id);
                const callOpener = `Hey ${hotLead.name.split(" ")[0]}, I was just reviewing your file and wanted to personally reach out — you're one of our highest-priority accounts right now. Do you have 5 minutes?`;
                return (
                  <div data-testid="war-room-hot-lead-alert" className="bg-red-600/10 border border-red-500/30 rounded-2xl overflow-hidden">
                    <div className="flex items-center justify-between gap-4 px-5 py-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-xl shrink-0">🔥</span>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-red-300">WAR ROOM — Hot Lead Alert</p>
                          <p className="text-xs text-red-300/70 truncate"><span className="font-semibold text-red-200">{hotLead.name}</span> is ready to close — score {hotLead.score}/100</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          data-testid="button-war-room-open-lead"
                          onClick={() => { if (hotLeadObj) openLeadDetail(hotLeadObj); }}
                          className="text-[10px] font-bold text-red-300 border border-red-500/40 rounded-lg px-2 py-1 bg-red-500/10 hover:bg-red-500/20 transition-colors"
                        >Act Now</button>
                        <button
                          data-testid="button-war-room-dismiss"
                          onClick={() => setWarRoomDismissedIds(prev => new Set([...prev, hotLead.id]))}
                          className="text-red-400/50 hover:text-red-300 text-xs px-1 py-1 transition-colors"
                          title="Dismiss"
                        >✕</button>
                      </div>
                    </div>
                    <div className="border-t border-red-500/20 px-5 py-3 space-y-2">
                      <p className="text-[10px] font-bold text-red-400/60 uppercase tracking-wider">Suggested Call Opener</p>
                      <p data-testid="text-war-room-call-opener" className="text-xs text-red-200/80 italic leading-relaxed">"{callOpener}"</p>
                      <div className="flex gap-2 pt-1">
                        <button
                          data-testid="button-copy-call-opener"
                          onClick={() => { navigator.clipboard.writeText(callOpener).catch(() => {}); }}
                          className="text-[10px] font-bold text-red-300/80 border border-red-500/30 rounded-lg px-3 py-1.5 hover:bg-red-500/10 transition-colors flex items-center gap-1"
                        >📋 Copy Opener</button>
                        <button
                          data-testid="button-war-room-draft-email"
                          onClick={() => { if (hotLeadObj) { openLeadDetail(hotLeadObj); setTimeout(() => setNurtureInput(`Urgent follow-up for ${hotLead.name} — hot lead at score ${hotLead.score}. Write a compelling closing email.`), 200); } }}
                          className="text-[10px] font-bold text-red-300/80 border border-red-500/30 rounded-lg px-3 py-1.5 hover:bg-red-500/10 transition-colors flex items-center gap-1"
                        >✉️ Draft Close Email</button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Standard pipeline alerts */}
              <div className="flex flex-wrap gap-3" data-testid="rogers-pipeline-alerts">
                {pipelineData.overdueFollowUps > 0 && (
                  <div data-testid="overdue-followups-badge" className="flex items-center gap-2 bg-orange-600/15 border border-orange-500/30 rounded-xl px-4 py-2 text-sm">
                    <span className="text-orange-400 font-bold">{pipelineData.overdueFollowUps}</span>
                    <span className="text-orange-300/80">overdue follow-up{pipelineData.overdueFollowUps !== 1 ? "s" : ""}</span>
                  </div>
                )}
                {pipelineData.hottestLead && pipelineData.hottestLead.score > 0 && pipelineData.hottestLead.score < 80 && (
                  <div
                    data-testid="hottest-lead-badge"
                    className="flex items-center gap-2 bg-emerald-600/15 border border-emerald-500/30 rounded-xl px-4 py-2 text-sm cursor-pointer hover:bg-emerald-600/25 transition-colors"
                    onClick={() => { const l = leads?.find(x => x.id === pipelineData.hottestLead!.id); if (l) openLeadDetail(l); }}
                  >
                    <span className="text-emerald-400">🔥</span>
                    <span className="text-emerald-300/80">Hottest: <span className="font-semibold text-emerald-300">{pipelineData.hottestLead.name}</span></span>
                    <span className="text-xs text-emerald-400/60">score {pipelineData.hottestLead.score}</span>
                  </div>
                )}
                {pipelineData.activeLeads > 0 && (
                  <div data-testid="active-leads-badge" className="flex items-center gap-2 bg-blue-600/10 border border-blue-500/20 rounded-xl px-4 py-2 text-sm">
                    <span className="text-blue-400 font-bold">{pipelineData.activeLeads}</span>
                    <span className="text-blue-300/70">active lead{pipelineData.activeLeads !== 1 ? "s" : ""}</span>
                  </div>
                )}
                {pipelineData.estimatedPipelineValue > 0 && (
                  <div data-testid="pipeline-value-badge" className="flex items-center gap-2 bg-purple-600/10 border border-purple-500/20 rounded-xl px-4 py-2 text-sm">
                    <span className="text-purple-300/70">est. pipeline</span>
                    <span className="text-purple-300 font-bold">${pipelineData.estimatedPipelineValue.toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-2xl p-4 md:p-6 max-w-xl w-full mx-4 space-y-4" data-testid="lead-form">
          <div className="flex items-center justify-between"><h2 className="text-lg font-display font-bold text-foreground">Add New Lead</h2><button data-testid="button-close-lead-modal" onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground transition-colors"><X size={18} /></button></div>
          <input
            data-testid="input-lead-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70"
          />
          <input
            data-testid="input-lead-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            type="email"
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70"
          />
          <input
            data-testid="input-lead-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone number (optional)"
            className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70"
          />
          <div className="space-y-2">
            <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Source</label>
            <div className="flex flex-wrap gap-2">
              {["manual", "referral", "linkedin", "cold-email"].map((s) => (
                <button
                  key={s}
                  data-testid={`button-source-${s}`}
                  onClick={() => setSource(s)}
                  className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    source === s
                      ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                      : "bg-muted/50 text-muted-foreground border border-border hover:text-foreground hover:border-border"
                  }`}
                >
                  {SOURCE_LABELS[s] ?? s}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="button-create-lead"
              onClick={handleCreateLead}
              disabled={createLeadMutation.isPending || !name.trim() || !email.trim()}
              className="bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-bold rounded-xl px-5 py-2.5 text-sm flex items-center gap-2 transition-colors"
            >
              {createLeadMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Add Lead
            </button>
            <button
              data-testid="button-cancel-lead"
              onClick={() => setShowForm(false)}
              className="text-muted-foreground hover:text-foreground font-bold rounded-xl px-5 py-2.5 text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
          </div>
        </div>
      )}

      <div className="space-y-4" data-testid="filter-section">
        <div className="flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-0 sm:min-w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
            <input
              data-testid="input-search-leads"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl pl-10 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70"
            />
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="button-filter-all"
            onClick={() => setSelectedStatusFilter(null)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              selectedStatusFilter === null
                ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                : "bg-muted/50 text-muted-foreground border border-border hover:text-foreground hover:border-border"
            }`}
          >
            All
          </button>
          {STATUS_OPTIONS.map((status) => (
            <button
              key={status}
              data-testid={`button-filter-${status.toLowerCase()}`}
              onClick={() => setSelectedStatusFilter(status)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                selectedStatusFilter === status
                  ? `${STATUS_COLORS[status].bg} ${STATUS_COLORS[status].text} border ${STATUS_COLORS[status].border}`
                  : "bg-muted/50 text-muted-foreground border border-border hover:text-foreground hover:border-border"
              }`}
            >
              {status}
            </button>
          ))}
        </div>
        {/* Source filter row */}
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-[10px] font-bold text-muted-foreground/50 uppercase tracking-wider pr-1">Source</span>
          {[null, "site-agent", "funnel", "manual", "linkedin", "referral", "cold-email"].map((src) => (
            <button
              key={src ?? "all"}
              data-testid={`button-source-filter-${src ?? "all"}`}
              onClick={() => setSourceFilter(src)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                sourceFilter === src
                  ? "bg-emerald-600/20 text-emerald-400 border border-emerald-500/30"
                  : "bg-muted/50 text-muted-foreground border border-border hover:text-foreground"
              }`}
            >
              {src === null ? 'All' : SOURCE_LABELS[src] ?? src}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array(4)
            .fill(0)
            .map((_, i) => (
              <div key={i} className="h-16 bg-muted/50 animate-pulse rounded-xl" />
            ))}
        </div>
      ) : filteredLeads && filteredLeads.length > 0 ? (
        <div className="space-y-2" data-testid="leads-list">
          {filteredLeads.map((lead: Lead) => (
            <div
              key={lead.id}
              className="bg-card/50 border border-border rounded-xl px-5 py-4 flex items-center justify-between gap-4 cursor-pointer hover:border-border transition-colors"
              data-testid={`lead-row-${lead.id}`}
              onClick={() => openLeadDetail(lead)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm font-bold text-foreground">{lead.name}</span>
                  {lead.status === "hot" ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-md border bg-red-500/20 text-red-400 border-red-500/40 animate-pulse uppercase" data-testid={`badge-hot-${lead.id}`}>
                      HIGH PRIORITY
                    </span>
                  ) : (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md uppercase ${STATUS_STYLES[lead.status] || "bg-muted/50 text-muted-foreground/70"}`}>
                      {lead.status}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 md:gap-3 mt-1 text-xs text-muted-foreground/70 flex-wrap">
                  <span className="truncate">{lead.email}</span>
                  {lead.phone && <span>{lead.phone}</span>}
                  <span>{lead.source}</span>
                  <span className="flex items-center gap-1">
                    <Clock size={12} />
                    {lead.nurtureCadenceDays ? `${lead.nurtureCadenceDays}d cadence` : "No cadence"}
                  </span>
                  {(lead as any).sequenceType && (lead as any).sequenceType !== "welcome" && (
                    <span className="text-amber-400/60 capitalize">{(lead as any).sequenceType}</span>
                  )}
                  {(lead as any).smsOptedOut && (
                    <span className="flex items-center gap-1 text-[10px] text-red-400/80 bg-red-500/10 border border-red-500/20 px-1.5 py-0.5 rounded-md" data-testid={`badge-sms-opted-out-${lead.id}`}>
                      <PhoneOff size={9} /> SMS Opted Out
                    </span>
                  )}
                </div>
              </div>
              <ArrowLeft size={16} className="text-muted-foreground/50 rotate-180 shrink-0" />
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card/50 border border-border rounded-2xl p-12 text-center" data-testid="no-leads">
          <MessageSquare size={40} className="text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground/70 text-sm">
            {leads && leads.length > 0 ? "No leads match your filters." : "No leads yet. Add your first lead above."}
          </p>
          {leads && leads.length === 0 && (
            <button
              data-testid="cta-add-lead"
              onClick={() => setShowForm(!showForm)}
              className="mt-4 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl text-sm flex items-center gap-2 transition-colors mx-auto"
            >
              <Plus size={16} />
              Add Lead
            </button>
          )}
        </div>
      )}
      </>)}
    </div>
  );
}
